/**
 * TanStack Query hooks for channels. Keys are namespaced "channels/…".
 *
 * The raw source snapshot is not cached: it is an audited decryption that the
 * contract marks `no-store`, so it is fetched on an explicit reveal, kept for
 * that view only (`gcTime: 0`), and never retried — a 410 means the retained
 * envelope is gone, and asking again cannot bring it back.
 */
import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";

import { useCursorList, type CursorQueryResult } from "@/features/shared/ops-api";

import {
  getChannelOrder,
  getChannelOrderRawSnapshot,
  getChannelSourceSet,
  listChannelOrders,
  listChannels,
  listReconciliationReports,
  type ChannelOrderDetail,
  type ChannelOrderFilters,
  type ChannelOrderRawSnapshot,
  type ChannelOrderSummary,
  type ChannelSourceSet,
  type ChannelSummary,
  type ReconciliationFilters,
  type ReconciliationReport,
} from "./api";

export const channelKeys = {
  channels: {
    all: ["channels/channels"] as const,
    list: () => ["channels/channels", "list"] as const,
    sourceSet: (channelId: string) => ["channels/channels", "source-set", channelId] as const,
    reports: (channelId: string, filters: Record<string, unknown>) =>
      ["channels/channels", "reports", channelId, filters] as const,
  },
  orders: {
    all: ["channels/orders"] as const,
    list: (filters: Record<string, unknown>) => ["channels/orders", "list", filters] as const,
    detail: (channelOrderId: string) => ["channels/orders", "detail", channelOrderId] as const,
    raw: (channelOrderId: string) => ["channels/orders", "raw", channelOrderId] as const,
  },
};

/** Channels are few and are drawn as a picker, so one bounded page is enough. */
export function useChannels(enabled = true) {
  return useQuery<ChannelSummary[]>({
    queryKey: channelKeys.channels.list(),
    queryFn: async ({ signal }) => [...(await listChannels({ limit: 200, signal })).items],
    enabled,
  });
}

/**
 * id → channel name for screens that only hold a `channel_id`. Callers gate it
 * on `channels.channel.read`; without the grant the id itself is shown rather
 * than firing a query that would 403.
 */
export function useChannelNames(enabled = true) {
  const query = useChannels(enabled);
  const channels = query.data;
  const channelName = useCallback(
    (channelId: string | undefined): string => {
      if (channelId === undefined) return "";
      return channels?.find((channel) => channel.id === channelId)?.name ?? channelId;
    },
    [channels],
  );
  return { channelName, channels: channels ?? [], isLoading: query.isLoading };
}

export function useChannelSourceSet(channelId: string | undefined, enabled = true) {
  return useQuery<ChannelSourceSet>({
    queryKey: channelKeys.channels.sourceSet(channelId ?? ""),
    queryFn: ({ signal }) => getChannelSourceSet(channelId ?? "", signal),
    enabled: enabled && channelId !== undefined && channelId !== "",
  });
}

export function useChannelOrderList(
  filters: ChannelOrderFilters,
  limit?: number,
): CursorQueryResult<ChannelOrderSummary> {
  return useCursorList<ChannelOrderSummary>(
    channelKeys.orders.list(filters),
    (args) => listChannelOrders(filters, args),
    limit === undefined ? {} : { limit },
  );
}

export function useChannelOrder(channelOrderId: string | undefined) {
  return useQuery<ChannelOrderDetail>({
    queryKey: channelKeys.orders.detail(channelOrderId ?? ""),
    queryFn: ({ signal }) => getChannelOrder(channelOrderId ?? "", signal),
    enabled: channelOrderId !== undefined && channelOrderId !== "",
  });
}

/** Enabled only while the operator is actively revealing; every enable is one
 *  audited decryption. */
export function useChannelOrderRaw(channelOrderId: string, enabled: boolean) {
  return useQuery<ChannelOrderRawSnapshot>({
    queryKey: channelKeys.orders.raw(channelOrderId),
    queryFn: ({ signal }) => getChannelOrderRawSnapshot(channelOrderId, undefined, signal),
    enabled,
    gcTime: 0,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export function useReconciliationReports(
  channelId: string | undefined,
  filters: ReconciliationFilters,
  limit?: number,
): CursorQueryResult<ReconciliationReport> {
  return useCursorList<ReconciliationReport>(
    channelKeys.channels.reports(channelId ?? "", filters),
    (args) => listReconciliationReports(channelId ?? "", filters, args),
    {
      ...(limit === undefined ? {} : { limit }),
      enabled: channelId !== undefined && channelId !== "",
    },
  );
}
